import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Lock, MailCheck } from 'lucide-react';
import { useKitUnlock } from '../hooks/useKitUnlock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Email-capture modal that unlocks the gated Executor Kit documents.
 *
 * Two-purpose: (1) enrolls the reader into the "executor" outreach drip via
 * POST /executor-kit/unlock, (2) sets the localStorage flag so they don't
 * see the modal again on this browser.
 */
const KitUnlockModal = ({ open, onOpenChange, docTitle, onUnlocked }) => {
  const { setUnlocked } = useKitUnlock();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/executor-kit/unlock`, {
        email: email.trim(),
        first_name: firstName.trim() || null,
      });
      setUnlocked({ email: data.email, token: data.unlock_token, at: Date.now() });
      onUnlocked?.();
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="kit-unlock-modal">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
            <Lock className="w-5 h-5 text-emerald-700" />
          </div>
          <DialogTitle className="text-center font-heading text-2xl text-ep-text">
            Unlock the full Executor Success Kit
          </DialogTitle>
          <DialogDescription className="text-center text-ep-text-muted">
            {docTitle
              ? <>Enter your email to open <span className="font-medium text-ep-text">{docTitle}</span> and get the rest of the kit sent to you.</>
              : <>Enter your email once and every guide in the kit is yours — no account required.</>}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2" data-testid="kit-unlock-form">
          <div>
            <Label htmlFor="unlock-first-name" className="text-sm text-ep-text-secondary">First name (optional)</Label>
            <Input
              id="unlock-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Jamie"
              className="mt-1"
              data-testid="unlock-first-name-input"
            />
          </div>
          <div>
            <Label htmlFor="unlock-email" className="text-sm text-ep-text-secondary">Email address</Label>
            <Input
              id="unlock-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1"
              data-testid="unlock-email-input"
            />
          </div>

          {error && <p className="text-sm text-red-600" data-testid="unlock-error">{error}</p>}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-ep-primary text-white hover:bg-ep-primary-dark"
            data-testid="unlock-submit-btn"
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Unlocking…</>
              : <><MailCheck className="w-4 h-4 mr-2" /> Send me the full kit</>}
          </Button>

          <p className="text-xs text-ep-text-muted text-center">
            We'll email the kit and a few practical follow-up tips. One click unsubscribes.
            No credit card, no account — just the guides.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default KitUnlockModal;
